import { useState } from "react";
import clsx from "clsx";

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "../base/Button";
import { TextField } from "../base/TextField";
import {
  ITitlePreviewExampleData,
  ITitlePreviewResult,
} from "../../models/Category/TitleConfig";

interface LiveTitlePreviewProps {
  exampleData: ITitlePreviewExampleData;
  result: ITitlePreviewResult | undefined;
  isGenerating: boolean;
  compiledPrompt: string | null;
  onExampleDataChange: (patch: Partial<ITitlePreviewExampleData>) => void;
  onGenerate: () => void;
}

/** The chips shown above the preview, in the order the mockup lists them. */
const CHIP_FIELDS: {
  key: keyof ITitlePreviewExampleData;
  label: string;
}[] = [
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "mk", label: "MK" },
  { key: "year", label: "Year" },
  { key: "placement", label: "Placement" },
  { key: "aspects", label: "Aspects" },
  { key: "partNumber", label: "Part Number" },
  { key: "colour", label: "Colour" },
];

export default function LiveTitlePreview({
  exampleData,
  result,
  isGenerating,
  compiledPrompt,
  onExampleDataChange,
  onGenerate,
}: LiveTitlePreviewProps) {
  const [editingExample, setEditingExample] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const overLimit =
    !!result && result.characterCount > result.characterLimit;

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          Live Title Preview
        </h2>
        <Button
          variant="outlined"
          size="xs"
          onClick={() => setEditingExample((value) => !value)}
        >
          {editingExample ? "Done" : "Example Data Used"}
        </Button>
      </div>

      {editingExample ? (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {CHIP_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              small
              fullWidth
              label={label}
              value={(exampleData[key] as string) ?? ""}
              onChange={(e) =>
                onExampleDataChange({ [key]: e.currentTarget.value })
              }
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {CHIP_FIELDS.filter(({ key }) => !!exampleData[key]).map(
            ({ key, label }) => (
              <span
                key={key}
                className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700"
              >
                <span className="text-gray-500">{label}:</span>{" "}
                {exampleData[key]}
              </span>
            ),
          )}
        </div>
      )}

      <Button fullWidth size="sm" onClick={onGenerate} disabled={isGenerating}>
        {isGenerating ? "Generating…" : "Generate Preview Title"}
      </Button>
      <p className="mt-1 text-xs text-gray-500">
        Each preview is a live AI call using the prompt below, so it is not run
        automatically as you type.
      </p>

      {result && (
        <>
          <p className="mt-4 text-sm font-medium">
            Preview{" "}
            <span
              className={clsx(
                "font-normal",
                overLimit ? "text-red-600" : "text-gray-500",
              )}
            >
              ({result.characterCount} / {result.characterLimit} characters)
            </span>
          </p>

          <div
            className={clsx(
              "mt-1 flex items-start gap-2 rounded border p-3",
              overLimit
                ? "border-red-200 bg-red-50"
                : "border-green-200 bg-green-50",
            )}
          >
            <p className="flex-1 text-sm">{result.title}</p>
            {overLimit ? (
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-600" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />
            )}
          </div>

          {result.includedFields.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium">How it was built</p>
              <p className="mt-1 text-xs text-orange-700">
                {result.includedFields.join(" + ")}
              </p>
            </div>
          )}

          {result.excludedFields.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium">What was left out</p>
              <ul className="mt-1 space-y-0.5">
                {result.excludedFields.map((exclusion) => (
                  <li key={exclusion.field} className="text-xs text-gray-600">
                    <span className="font-medium">{exclusion.field}</span> —{" "}
                    {exclusion.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.warnings.length > 0 && (
            <ul className="mt-3 space-y-1">
              {result.warnings.map((warning) => (
                <li
                  key={warning}
                  className="flex gap-1.5 text-xs text-amber-700"
                >
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-3 text-xs text-gray-500">
            Score {result.score} / 100. Character count includes spaces.
          </p>
        </>
      )}

      {compiledPrompt && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={() => setShowPrompt((value) => !value)}
            className="text-xs text-orange-700 hover:underline"
          >
            {showPrompt ? "Hide" : "Show"} the compiled prompt
          </button>
          <p className="mt-1 text-xs text-gray-500">
            This exact text is what runs in production once saved and synced.
          </p>
          {showPrompt && (
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-700">
              {compiledPrompt}
            </pre>
          )}
        </div>
      )}
    </section>
  );
}
